import { useAppContext } from "src/hooks/useAppContext";
import { getIndustryBg } from "../utils";
import { motion, AnimatePresence } from "framer-motion";
import { WeatherSidebar } from "./WeatherSidebar";
import { NewsBottomSection } from "./NewsBottomSection";
import { ClockBottomSection } from "./ClockBottomSection";
import { ChannelLayout } from "src/types.g";
import styled from "styled-components";
import { PresetColors } from "../Styling";

interface ZonePreview {
  preview: React.ReactNode;
  label: string;
  key: ChannelLayout;
}

const getScaleTransform = (baseScale: number) => `
  transform: scale(${baseScale});
`;

const PreviewContainer = styled.div`
  width: 339px;
  height: 100%;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  background: var(--color-white-100, #fff);
`;

const TemplateImageWrapper = styled(motion.div)<{
  baseWidth?: number;
  baseHeight?: number;
}>`
  transform-origin: top left;
  width: ${(props) => `${props.baseWidth}px` || "100%"};
  height: ${(props) => `${props.baseHeight}px` || "100%"};
  position: absolute;
  top: 0;
  ${getScaleTransform(1)}
`;

const StyledImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const IndustryBackground = styled.img`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 0;
  object-fit: cover;
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1;
  mix-blend-mode: multiply;
`;

const MultiZoneContainer = styled.div`
  width: calc(100% - 8px);
  height: calc(100% - 12px);
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  position: relative;
  z-index: 3;
`;

const WeatherWrapper = styled(motion.div)`
  position: absolute;
  top: 0px;
  right: 0px;
  transform-origin: top right;
  transform: scale(0.47);
  ${getScaleTransform(0.47)}
`;

const NewsWrapper = styled.div<{ width: string }>`
  position: absolute;
  transform: scale(0.45);
  transform-origin: left bottom;
  bottom: 0;
  background: white;
  width: ${(props) => props.width};
  ${getScaleTransform(0.45)}
`;

const ClockWrapper = styled.div`
  position: absolute;
  transform-origin: right bottom;
  bottom: 0;
  right: 0;
  width: 187px;
  height: 49px;
  transform: scale(0.45);
  ${getScaleTransform(0.45)}
`;

const BasePreviewContainer: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <PreviewContainer>{children}</PreviewContainer>;

const TemplateImage: React.FC<{
  src: string;
  baseWidth?: number;
  baseHeight?: number;
}> = ({ src, baseWidth, baseHeight }) => (
  <AnimatePresence initial={false}>
    <TemplateImageWrapper
      baseWidth={baseWidth}
      baseHeight={baseHeight}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <StyledImage src={src} />
    </TemplateImageWrapper>
  </AnimatePresence>
);

export const getZonesPreview = ({
  templatePlaceholder,
  logoThumbnail,
  presetColors,
}: {
  templatePlaceholder: string;
  logoThumbnail: string | null;
  presetColors: PresetColors;
}) => {
  const context = useAppContext();
  const industry = context.currentOrg?.preferences?.settings?.industry_name;
  const industryBg = getIndustryBg(industry);
  const { primary, secondary } = presetColors;

  const createMultiZoneLayout = (children: React.ReactNode) => (
    <BasePreviewContainer>
      <Overlay />
      <IndustryBackground src={industryBg} alt="industryBg" loading="eager" />
      <MultiZoneContainer>{children}</MultiZoneContainer>
    </BasePreviewContainer>
  );

  const zones: ZonePreview[] = [
    {
      preview: (
        <BasePreviewContainer>
          <div style={{ width: "100%", height: "100%", position: "relative" }}>
            <TemplateImage
              src={templatePlaceholder}
              baseWidth={339}
              baseHeight={190}
            />
          </div>
        </BasePreviewContainer>
      ),
      key: ChannelLayout.OneZone,
      label: "1 Main Zone",
    },
    {
      preview: createMultiZoneLayout(
        <>
          <div style={{ width: "100%", height: "100%" }}>
            <TemplateImage
              src={templatePlaceholder}
              baseWidth={240}
              baseHeight={150}
            />
          </div>
          <WeatherWrapper
            key="weather"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <WeatherSidebar primaryColor={primary} secondaryColor={secondary} />
          </WeatherWrapper>
          <NewsWrapper width="730px">
            <NewsBottomSection primaryColor={primary} logo={logoThumbnail} />
          </NewsWrapper>
        </>,
      ),
      key: ChannelLayout.ThreeZone,
      label: "3 Zone Layout",
    },
    {
      preview: createMultiZoneLayout(
        <>
          <div>
            <TemplateImage
              src={templatePlaceholder}
              baseWidth={240}
              baseHeight={150}
            />
          </div>
          <WeatherWrapper
            key="weather"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <WeatherSidebar primaryColor={primary} secondaryColor={secondary} />
          </WeatherWrapper>
          <NewsWrapper width="533px">
            <NewsBottomSection primaryColor={primary} logo={logoThumbnail} />
          </NewsWrapper>
          <ClockWrapper>
            <ClockBottomSection
              primaryColor={primary}
              secondaryColor={secondary}
            />
          </ClockWrapper>
        </>,
      ),
      key: ChannelLayout.FourZone,
      label: "4 Zone Layout",
    },
  ];

  return zones;
};
